import React, { useState } from 'react';
import { Language, isLanguageSupported } from '../lib/i18n';
import { normalizeLanguageCode } from '../lib/i18n/languageDetection';

interface LanguageSwitcherProps {
  currentLanguage: Language;
  supportedLanguages: string; // comma-separated language codes
  onLanguageChange: (language: Language) => void;
  style?: {
    primaryColor?: string;
    backgroundColor?: string;
  };
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  currentLanguage,
  supportedLanguages,
  onLanguageChange,
  style = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Parse supported languages from comma-separated string
  const languages = supportedLanguages
    .split(',')
    .map(lang => normalizeLanguageCode(lang))
    .filter(lang => isLanguageSupported(lang)) as Language[];

  // Don't render if less than 2 languages
  if (languages.length < 2) {
    return null;
  }

  // Language display names
  const languageNames: Record<Language, string> = {
    ro: 'RO',
    en: 'EN', 
    de: 'DE',
    fr: 'FR',
    es: 'ES',
    it: 'IT'
  };

  const handleLanguageSelect = (language: Language) => {
    onLanguageChange(language);
    setIsOpen(false);
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-block',
    zIndex: 1000,
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    color: 'white',
    padding: '4px 8px',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: '45px',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  };

  const buttonHoverStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  };

  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: 0,
    backgroundColor: style.backgroundColor || 'white',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    minWidth: '60px',
    marginTop: '4px',
    overflow: 'hidden',
  };

  const menuItemStyle: React.CSSProperties = {
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '12px',
    textAlign: 'center',
    transition: 'background-color 0.2s ease',
    color: '#333',
  };

  const menuItemHoverStyle: React.CSSProperties = {
    backgroundColor: style.primaryColor ? `${style.primaryColor}15` : 'rgba(0, 0, 0, 0.05)',
  };

  return (
    <div style={dropdownStyle}>
      <button
        style={buttonStyle}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={(e) => {
          Object.assign(e.currentTarget.style, buttonHoverStyle);
        }}
        onMouseLeave={(e) => {
          Object.assign(e.currentTarget.style, buttonStyle);
        }}
        aria-label="Change language"
      >
        {languageNames[currentLanguage]}
        <span 
          style={{ 
            fontSize: '10px', 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        >
          ▼
        </span>
      </button>
      
      {isOpen && (
        <div style={menuStyle}>
          {languages.map((language) => (
            <div
              key={language}
              style={menuItemStyle}
              onClick={() => handleLanguageSelect(language)}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, menuItemHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, menuItemStyle);
              }}
            >
              {languageNames[language]}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
